import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { ShieldCheck, Ruler, Users, Award } from "lucide-react";
import { useTexts, useExtras } from "@/lib/texts";

const EASE = [0.16, 1, 0.3, 1];
const CRED_ICONS = [ShieldCheck, Ruler, Users, Award];

const Counter = ({ to, suffix }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-60px" });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, to, {
            duration: 1.8,
            ease: EASE,
            onUpdate: (v) => setVal(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, to]);

    return (
        <span ref={ref}>
            {val}
            {suffix}
        </span>
    );
};

export const WhyUs = () => {
    const texts = useTexts();
    const { whyus } = useExtras();

    return (
    <section
        id="trent-anni"
        data-testid="why-us-section"
        className="bg-[#F0EDE6] py-20 sm:py-28 lg:py-36"
    >
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
            <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, ease: EASE }}
                        className="font-mono-alt text-xs uppercase tracking-[0.3em] text-[#C24E0F]"
                    >
                        Perché Noi
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        className="mt-4 font-display text-3xl font-light tracking-tight text-stone-900 sm:text-4xl lg:text-5xl"
                    >
                        {texts.whyus_heading}
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
                        className="mt-6 max-w-md text-base leading-relaxed text-stone-600"
                    >
                        {texts.whyus_paragraph}
                    </motion.p>

                    <div className="mt-10 space-y-6">
                        {whyus.credentials.map((c, i) => {
                            const Icon = CRED_ICONS[i % CRED_ICONS.length];
                            return (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -24 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.7, delay: 0.15 + i * 0.12, ease: EASE }}
                                    className="flex items-start gap-4"
                                    data-testid={`why-credential-${i}`}
                                >
                                    <Icon className="mt-1 h-5 w-5 shrink-0 text-[#C24E0F]" />
                                    <div>
                                        <h3 className="font-display text-lg font-medium text-stone-900">
                                            {c.title}
                                        </h3>
                                        <p className="mt-1 text-sm text-stone-600">{c.desc}</p>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-2 content-start gap-px self-start overflow-hidden border border-stone-900/10 bg-stone-900/10 lg:col-span-7">
                    {whyus.stats.map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0.94 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, delay: i * 0.1, ease: EASE }}
                            data-testid={`why-stat-${i}`}
                            className="flex flex-col items-center justify-center bg-[#F9F7F2] px-6 py-14 text-center transition-colors duration-500 hover:bg-[#1C1917] [&:hover_.stat-value]:text-[#E75C15] [&:hover_.stat-label]:text-stone-300"
                        >
                            <span className="stat-value font-display text-5xl font-semibold text-stone-900 transition-colors duration-500 sm:text-6xl">
                                <Counter to={Number(s.value) || 0} suffix={s.suffix} />
                            </span>
                            <span className="stat-label mt-3 font-mono-alt text-[11px] uppercase tracking-[0.2em] text-stone-500 transition-colors duration-500">
                                {s.label}
                            </span>
                        </motion.div>
                    ))}
                </div>
            </div>
        </div>
    </section>
    );
};
